import * as React from "react"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql, withPrefix } from "gatsby"
import { useLocalization } from "gatsby-theme-i18n"

const SEO = ({ location, pageContext }) => {
  const { locale, config, defaultLang, prefixDefault } = useLocalization()
  const data = useStaticQuery(graphql`
    query LocalizationSEOQuery {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `)
  const defaultSiteUrl = data.site.siteMetadata.siteUrl

  // Fallback URLs (e.g. /hu/scriptures/<slug>/en/) exist so users browsing in
  // one UI locale can still read content that has no translation in that
  // locale. They are not language alternatives — the scripture template already
  // emits rel=canonical pointing them at the primary URL. Stay out of the
  // hreflang cluster entirely to avoid contradicting that signal.
  if (pageContext.isFallback) {
    return (
      <Helmet>
        <html lang={pageContext.hrefLang} />
        <meta
          property="og:locale"
          content={pageContext.hrefLang.replace(`-`, `_`)}
        />
      </Helmet>
    )
  }

  // Emit hreflang only for locales whose translation actually exists. For
  // auto-created pages (no availableLocales passed in), default to all
  // configured locales — those URLs exist by construction via the theme.
  const availableLocales =
    pageContext.availableLocales || config.map((c) => c.code)
  const alternates = config.filter((c) => availableLocales.includes(c.code))

  const hrefFor = (code) => {
    if (code === defaultLang && !prefixDefault) {
      return `${defaultSiteUrl}${
        pageContext.originalPath === withPrefix(`/`)
          ? ``
          : pageContext.originalPath
      }`
    }
    return `${defaultSiteUrl}${withPrefix(`/${code}${pageContext.originalPath}`)}`
  }

  // x-default: the page to serve a user whose language/region isn't covered.
  // Prefer English when available, otherwise fall back to the first existing
  // translation. Pointing at the bare site root (the previous behaviour) was
  // meaningless for per-content pages.
  const xDefaultLocale = availableLocales.includes(`en`)
    ? `en`
    : availableLocales[0]

  const { pathname } = location
  const selfHref = `${defaultSiteUrl}${
    pathname === withPrefix(`/`) ? `` : pathname
  }`

  return (
    <Helmet>
      <html lang={pageContext.hrefLang} />
      <link
        rel="alternate"
        hrefLang="x-default"
        href={hrefFor(xDefaultLocale)}
      />
      <link
        rel="alternate"
        hrefLang={pageContext.hrefLang}
        href={selfHref}
      />
      {alternates.map((l) => {
        if (l.code === locale) return null
        return (
          <link
            key={l.code}
            rel="alternate"
            hrefLang={l.hrefLang}
            href={hrefFor(l.code)}
          />
        )
      })}
      <meta
        property="og:locale"
        content={pageContext.hrefLang.replace(`-`, `_`)}
      />
      {alternates.map((l) => {
        if (l.code === locale) return null
        return (
          <meta
            key={l.code}
            property="og:locale:alternate"
            content={l.hrefLang.replace(`-`, `_`)}
          />
        )
      })}
    </Helmet>
  )
}

export { SEO }
